<template>
  <div class="col-sm-12 dispatch-schedule-page">
    <div class="dispatch-schedule-container">
      <!-- =====================================================
           SECTION 1: SCHEDULE DETAILS
      ====================================================== -->
      <div class="section-card">
        <div class="section-header">
          <div>
            <h6>Schedule Details <span class="required-star">*</span></h6>

            <span>
              Select distributor, delivery route, effective date range and time
              slot. Click Search to load dealers and vehicles.
            </span>
          </div>
        </div>

        <div class="schedule-main-grid">
          <!-- Distributor -->
          <div class="form-item distributor-item">
            <label>Distributor <span class="required-star">*</span></label>

            <select
              v-model="selectedDistributorId"
              @change="onDistributorChange"
              :disabled="
                isDistributorLocked ||
                loadingDistributors ||
                loadingRoutes ||
                loadingDealers ||
                saving
              "
            >
              <option value="">Select Distributor</option>

              <option v-if="!isDistributorUser" :value="ALL">
                All Distributors
              </option>

              <option
                v-for="distributor in distributorList"
                :key="distributor.profile_id"
                :value="String(distributor.profile_id)"
              >
                {{ distributor.name }}
              </option>
            </select>
          </div>

          <!-- Delivery Route -->
          <div class="form-item route-item">
            <label>Delivery Route <span class="required-star">*</span></label>

            <div
              class="smart-multi"
              :class="{
                disabled:
                  !selectedDistributorId ||
                  selectedDistributorId === ALL ||
                  loadingRoutes ||
                  loadingDealers ||
                  saving,
                open: showRouteDropdown,
              }"
              @click.stop="toggleRouteDropdown"
            >
              <div class="smart-multi-content">
                <div v-if="selectedRoutes.length" class="smart-chip-list">
                  <span
                    v-for="routeId in selectedRoutes.slice(0, 2)"
                    :key="routeId"
                    class="smart-chip"
                  >
                    {{ getRouteName(routeId) }}
                  </span>

                  <span
                    v-if="selectedRoutes.length > 2"
                    class="smart-chip-more"
                  >
                    +{{ selectedRoutes.length - 2 }}
                  </span>
                </div>

                <span v-else class="smart-placeholder">
                  {{
                    loadingRoutes
                      ? "Loading routes..."
                      : selectedDistributorId === ALL
                      ? "Select one distributor first"
                      : selectedDistributorId
                      ? "Select delivery route"
                      : "Select distributor first"
                  }}
                </span>
              </div>

              <div class="smart-chevron">
                <i
                  class="fa"
                  :class="
                    showRouteDropdown ? 'fa-chevron-up' : 'fa-chevron-down'
                  "
                ></i>
              </div>

              <div
                v-if="showRouteDropdown"
                class="smart-menu smart-menu-left"
                @click.stop
              >
                <div class="smart-menu-header">
                  <div>
                    <strong>Delivery Routes</strong>
                    <span>{{ selectedRoutes.length }} selected</span>
                  </div>

                  <div class="smart-header-actions">
                    <button
                      type="button"
                      :disabled="!deliveryRouteList.length"
                      @click.stop="selectAllRoutes"
                    >
                      Select all
                    </button>

                    <button
                      type="button"
                      :disabled="!selectedRoutes.length"
                      @click.stop="clearRoutes"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div class="smart-option-list">
                  <button
                    v-for="route in deliveryRouteList"
                    :key="route.id"
                    type="button"
                    class="smart-option"
                    :class="{
                      selected: selectedRoutes.includes(String(route.id)),
                    }"
                    @click.stop="toggleRoute(route.id)"
                  >
                    <span class="smart-check">
                      <i class="fa fa-check"></i>
                    </span>

                    <span class="smart-option-text">
                      <strong>{{ route.name }}</strong>
                      <small>{{ route.description || "Delivery route" }}</small>
                    </span>
                  </button>

                  <div v-if="!deliveryRouteList.length" class="smart-empty">
                    {{
                      loadingRoutes
                        ? "Loading delivery routes..."
                        : "No delivery routes available."
                    }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Effective From -->
          <div class="form-item date-item">
            <label>Effective From <span class="required-star">*</span></label>

            <input
              type="date"
              v-model="effectiveFrom"
              :min="currentDate"
              :disabled="loadingDealers || saving"
              @change="handleScheduleDateChange"
            />
          </div>

          <!-- Effective To -->
          <div class="form-item date-item">
            <label>Effective To <span class="required-star">*</span></label>

            <input
              type="date"
              v-model="effectiveTo"
              :min="effectiveFrom || currentDate"
              :disabled="loadingDealers || saving"
              @change="handleScheduleDateChange"
            />
          </div>

          <!-- Time Slot -->
          <div class="form-item time-slot-item">
            <label>Time Slot <span class="required-star">*</span></label>

            <select
              v-model="selectedTimeSlot"
              :disabled="loadingTimeSlots || loadingDealers || saving"
              @change="onTimeSlotChange"
            >
              <option value="" disabled>
                {{
                  loadingTimeSlots
                    ? "Loading Time Slots..."
                    : "Select Time Slot"
                }}
              </option>

              <option
                v-for="slot in timeSlotList"
                :key="slot.id"
                :value="String(slot.id)"
              >
                {{ slot.label }}
              </option>
            </select>
          </div>

          <!-- Search -->
          <div class="form-item button-item schedule-search-item">
            <label class="hidden-label">Search</label>

            <button
              type="button"
              class="btn button-default"
              @click="searchScheduleFilters"
              :disabled="
                loadingDealers ||
                loadingDistributors ||
                loadingRoutes ||
                loadingTimeSlots ||
                saving
              "
            >
              <span
                v-if="loadingDealers"
                class="spinner-border spinner-border-sm"
              ></span>

              <span v-else>Search</span>
            </button>
          </div>
        </div>
      </div>

      <!-- =====================================================
           SECTION 2: VEHICLE SELECTION
      ====================================================== -->
      <div class="section-card">
        <div class="section-header">
          <div>
            <h6>Vehicle Selection <span class="required-star">*</span></h6>

            <span>
              Vehicles are loaded from the Schedule Details search response.
              Unavailable vehicles are already assigned to a schedule.
            </span>
          </div>

          <div class="section-count">
            {{
              filtersSearched
                ? selectedVehicles.length + " selected"
                : "Not selected"
            }}
          </div>
        </div>

        <div
          v-if="filtersSearched"
          class="selection-table-card vehicle-selection-card"
        >
          <div class="selection-table-toolbar">
            <div class="toolbar-title">
              <strong>Vehicles</strong>

              <span>
                {{ vehicleList.length }}
                {{ vehicleList.length === 1 ? "vehicle" : "vehicles" }}
              </span>
            </div>

            <div class="vehicle-table-actions">
              <button
                type="button"
                class="mini-action-button"
                :disabled="!selectableVehicleList.length || saving"
                @click="selectAllVehicles"
              >
                Select all
              </button>

              <button
                type="button"
                class="mini-action-button"
                :disabled="!selectedVehicles.length || saving"
                @click="clearVehicles"
              >
                Clear
              </button>
            </div>
          </div>

          <div class="table-scroll vehicle-table-scroll">
            <table class="selection-table vehicle-table">
              <thead>
                <tr>
                  <th class="select-column">
                    <input
                      type="checkbox"
                      :checked="isAllVehiclesSelected"
                      :disabled="!selectableVehicleList.length || saving"
                      @change="toggleAllVehicles"
                    />
                  </th>

                  <th>Vehicle</th>
                  <th>Mobile No</th>
                  <th>Ongoing Delivery Schedule</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="vehicle in vehicleList"
                  :key="vehicle.id"
                  :class="{
                    selected: selectedVehicles.includes(String(vehicle.id)),
                    'disabled-row': !canSelectVehicle(vehicle),
                  }"
                >
                  <td class="select-column">
                    <input
                      type="checkbox"
                      :value="String(vehicle.id)"
                      v-model="selectedVehicles"
                      :disabled="saving || !canSelectVehicle(vehicle)"
                    />
                  </td>

                  <td class="primary-cell">
                    {{ vehicle.name }}
                  </td>

                  <td>
                    <span class="vehicle-code">
                      {{ vehicle.code || "-" }}
                    </span>
                  </td>

                  <td class="schedule-list-cell">
                    <div
                      v-if="getScheduleNameList(vehicle).length"
                      class="schedule-chip-list"
                      :title="getScheduleNameList(vehicle).join(', ')"
                    >
                      <span
                        v-for="scheduleName in getScheduleNameList(vehicle)"
                        :key="scheduleName"
                        class="schedule-name-chip"
                        :title="scheduleName"
                      >
                        {{ scheduleName }}
                      </span>
                    </div>

                    <span v-else class="no-schedule-text">
                      No schedules
                    </span>
                  </td>

                  <td>
                    <span
                      class="availability-pill"
                      :class="getAvailabilityClass(vehicle)"
                    >
                      {{ getAvailabilityLabel(vehicle) }}
                    </span>
                  </td>
                </tr>

                <tr v-if="!vehicleList.length">
                  <td colspan="5" class="empty-table">
                    No vehicles found in selected schedule search response.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-else class="vehicle-wait-state">
          <span>
            Select schedule details and click Search to load vehicles.
          </span>
        </div>
      </div>

      <!-- =====================================================
           SECTION 3: DEALER SELECTION
      ====================================================== -->
      <div class="section-card">
        <div class="section-header">
          <div>
            <h6>Dealer Selection <span class="required-star">*</span></h6>

            <span>
              Dealers are loaded after Schedule Details search. Unavailable
              dealers are already assigned to a schedule.
            </span>
          </div>

          <div class="section-count">
            {{
              filtersSearched
                ? selectedDealerIds.length + " selected"
                : "Not selected"
            }}
          </div>
        </div>

        <div class="dealer-filter-grid">
          <div class="form-item dealer-search-item">
            <label>Search Dealer</label>

            <div class="search-input-wrap">
              <i class="fa fa-search"></i>

              <input
                type="text"
                v-model="dealerSearchText"
                placeholder="Filter loaded dealers..."
                :disabled="!filtersSearched || loadingDealers || saving"
              />
            </div>
          </div>
        </div>

        <div class="selection-table-card">
          <div class="selection-table-toolbar">
            <div class="toolbar-title">
              <strong>Dealers</strong>

              <span>
                {{ filteredDealerList.length }}
                {{ filteredDealerList.length === 1 ? "dealer" : "dealers" }}
              </span>
            </div>

            <div class="vehicle-table-actions">
              <button
                type="button"
                class="mini-action-button"
                :disabled="!selectableFilteredDealerList.length || saving"
                @click="selectAllVisibleDealers"
              >
                Select visible
              </button>

              <button
                type="button"
                class="mini-action-button"
                :disabled="!selectedDealerIds.length || saving"
                @click="clearDealers"
              >
                Clear
              </button>
            </div>
          </div>

          <div class="table-scroll dealer-table-scroll">
            <table class="selection-table dealer-table">
              <thead>
                <tr>
                  <th class="select-column">
                    <input
                      type="checkbox"
                      :checked="isAllDealersSelected"
                      :disabled="!selectableFilteredDealerList.length || saving"
                      @change="toggleAllDealers"
                    />
                  </th>

                  <th>Dealer Name</th>
                  <th>Route</th>
                  <th>Address</th>
                  <th>Ongoing Delivery Schedule</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="dealer in filteredDealerList"
                  :key="dealer.id"
                  :class="{
                    selected: selectedDealerIds.includes(String(dealer.id)),
                    'disabled-row': !canSelectDealer(dealer),
                  }"
                >
                  <td class="select-column">
                    <input
                      type="checkbox"
                      :value="String(dealer.id)"
                      v-model="selectedDealerIds"
                      :disabled="saving || !canSelectDealer(dealer)"
                    />
                  </td>

                  <td class="primary-cell">
                    {{ dealer.name }}
                  </td>

                  <td>
                    <span class="route-cell">
                      {{ formatDealerRoutes(dealer) }}
                    </span>
                  </td>

                  <td class="address-cell">
                    {{
                      dealer.address || dealer.address1 || dealer.area || "-"
                    }}
                  </td>

                  <td class="schedule-list-cell">
                    <div
                      v-if="getScheduleNameList(dealer).length"
                      class="schedule-chip-list"
                      :title="getScheduleNameList(dealer).join(', ')"
                    >
                      <span
                        v-for="scheduleName in getScheduleNameList(dealer)"
                        :key="scheduleName"
                        class="schedule-name-chip"
                        :title="scheduleName"
                      >
                        {{ scheduleName }}
                      </span>
                    </div>

                    <span v-else class="no-schedule-text">
                      No schedules
                    </span>
                  </td>

                  <td>
                    <span
                      class="availability-pill"
                      :class="getAvailabilityClass(dealer)"
                    >
                      {{ getAvailabilityLabel(dealer) }}
                    </span>
                  </td>
                </tr>

                <tr v-if="!filteredDealerList.length">
                  <td colspan="6" class="empty-table">
                    {{
                      filtersSearched
                        ? "No dealers found for selected route and date range."
                        : "Select distributor, delivery route and date range, then click Search to load dealers."
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- =====================================================
           SECTION 4: DELIVERY DATES
      ====================================================== -->
      <div class="section-card delivery-date-section">
        <div class="section-header">
          <div>
            <h6>Delivery Dates <span class="required-star">*</span></h6>

            <span>
              Select one delivery date set. The selected days/weeks/months are
              used automatically.
            </span>
          </div>

          <div class="section-count">
            {{ selectedDateSet ? "1 selected" : "Not selected" }}
          </div>
        </div>

        <div class="date-set-builder">
          <div class="date-set-builder-header">
            <div>
              <strong>Delivery Date Set</strong>

              <span>
                Changing the recurrence type clears the current date selection
                after confirmation.
              </span>
            </div>
          </div>

          <div class="frequency-selector">
            <button
              type="button"
              class="frequency-option"
              :class="{
                selected: draftDeliveryFrequency === 'day_by_day',
              }"
              @click="requestFrequencyChange('day_by_day')"
            >
              <span class="frequency-radio"></span>

              <span class="frequency-content">
                <strong>Day by Day</strong>
                <small>Select weekdays</small>
              </span>
            </button>

            <button
              type="button"
              class="frequency-option"
              :class="{
                selected: draftDeliveryFrequency === 'week_by_week',
              }"
              @click="requestFrequencyChange('week_by_week')"
            >
              <span class="frequency-radio"></span>

              <span class="frequency-content">
                <strong>Week By Week</strong>
                <small>Select weeks and weekdays</small>
              </span>
            </button>

            <button
              type="button"
              class="frequency-option"
              :class="{
                selected: draftDeliveryFrequency === 'month_by_month',
              }"
              @click="requestFrequencyChange('month_by_month')"
            >
              <span class="frequency-radio"></span>

              <span class="frequency-content">
                <strong>Month By Month</strong>
                <small>Select months, weeks and weekdays</small>
              </span>
            </button>
          </div>

          <div class="date-config-panel">
            <template v-if="draftDeliveryFrequency === 'day_by_day'">
              <div class="config-block">
                <div class="config-label-row">
                  <div>
                    <strong>Delivery Days</strong>
                    <span>Select one or more weekdays</span>
                  </div>

                  <small>{{ draftDeliveryDays.length }} selected</small>
                </div>

                <div class="chip-selector">
                  <label
                    v-for="day in deliveryDays"
                    :key="day.value"
                    class="choice-chip"
                    :class="{
                      selected: draftDeliveryDays.includes(day.value),
                    }"
                  >
                    <input
                      type="checkbox"
                      :value="day.value"
                      v-model="draftDeliveryDays"
                    />

                    <span>{{ day.label }}</span>
                  </label>
                </div>
              </div>
            </template>

            <template v-if="draftDeliveryFrequency === 'week_by_week'">
              <div class="config-grid">
                <div class="config-block">
                  <div class="config-label-row">
                    <div>
                      <strong>Weeks</strong>
                      <span>Select one or more weeks</span>
                    </div>

                    <small>{{ draftWeeks.length }} selected</small>
                  </div>

                  <div class="chip-selector">
                    <label
                      v-for="week in weekOptions"
                      :key="week.value"
                      class="choice-chip"
                      :class="{
                        selected: draftWeeks.includes(week.value),
                      }"
                    >
                      <input
                        type="checkbox"
                        :value="week.value"
                        v-model="draftWeeks"
                      />

                      <span>{{ week.label }}</span>
                    </label>
                  </div>
                </div>

                <div class="config-block">
                  <div class="config-label-row">
                    <div>
                      <strong>Delivery Days</strong>
                      <span>Select one or more weekdays</span>
                    </div>

                    <small>{{ draftDeliveryDays.length }} selected</small>
                  </div>

                  <div class="chip-selector">
                    <label
                      v-for="day in deliveryDays"
                      :key="day.value"
                      class="choice-chip"
                      :class="{
                        selected: draftDeliveryDays.includes(day.value),
                      }"
                    >
                      <input
                        type="checkbox"
                        :value="day.value"
                        v-model="draftDeliveryDays"
                      />

                      <span>{{ day.label }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </template>

            <template v-if="draftDeliveryFrequency === 'month_by_month'">
              <div class="month-config-stack">
                <div class="config-block">
                  <div class="config-label-row">
                    <div>
                      <strong>Months</strong>
                      <span>Select one or more months</span>
                    </div>

                    <small>{{ draftMonths.length }} selected</small>
                  </div>

                  <div class="chip-selector month-selector">
                    <label
                      v-for="month in monthOptions"
                      :key="month.value"
                      class="choice-chip month-chip"
                      :class="{
                        selected: draftMonths.includes(month.value),
                      }"
                    >
                      <input
                        type="checkbox"
                        :value="month.value"
                        v-model="draftMonths"
                      />

                      <span>{{ month.label }}</span>
                    </label>
                  </div>
                </div>

                <div class="config-grid">
                  <div class="config-block">
                    <div class="config-label-row">
                      <div>
                        <strong>Weeks</strong>
                        <span>Select one or more weeks</span>
                      </div>

                      <small>{{ draftWeeks.length }} selected</small>
                    </div>

                    <div class="chip-selector">
                      <label
                        v-for="week in weekOptions"
                        :key="week.value"
                        class="choice-chip"
                        :class="{
                          selected: draftWeeks.includes(week.value),
                        }"
                      >
                        <input
                          type="checkbox"
                          :value="week.value"
                          v-model="draftWeeks"
                        />

                        <span>{{ week.label }}</span>
                      </label>
                    </div>
                  </div>

                  <div class="config-block">
                    <div class="config-label-row">
                      <div>
                        <strong>Delivery Days</strong>
                        <span>Select one or more weekdays</span>
                      </div>

                      <small>{{ draftDeliveryDays.length }} selected</small>
                    </div>

                    <div class="chip-selector">
                      <label
                        v-for="day in deliveryDays"
                        :key="day.value"
                        class="choice-chip"
                        :class="{
                          selected: draftDeliveryDays.includes(day.value),
                        }"
                      >
                        <input
                          type="checkbox"
                          :value="day.value"
                          v-model="draftDeliveryDays"
                        />

                        <span>{{ day.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div v-if="selectedDateSet" class="added-batches-section">
          <div class="batch-section-header">
            <div>
              <strong>Selected Delivery Date Set</strong>
            </div>
          </div>

          <div class="batch-list">
            <div class="batch-card">
              <div class="batch-index">1</div>

              <div class="batch-content">
                <span
                  class="batch-type"
                  :class="getBatchTypeClass(selectedDateSet.type)"
                >
                  {{ getFrequencyLabel(selectedDateSet.type) }}
                </span>

                <strong>
                  {{ formatDeliveryBatch(selectedDateSet) }}
                </strong>
              </div>

              <button
                type="button"
                class="batch-remove-button"
                title="Clear date set"
                @click="clearSelectedDateSet"
              >
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
        </div>

        <div v-else class="no-date-sets">
          <span>Complete the date selection to create the date set.</span>
        </div>
      </div>

      <!-- =====================================================
           SECTION 5: REMARKS
      ====================================================== -->
      <div class="section-card">
        <div class="section-header">
          <div>
            <h6>Remarks</h6>

            <span>Add an optional remark for this schedule.</span>
          </div>

          <small class="optional-label">Optional</small>
        </div>

        <div class="remarks-box">
          <textarea
            v-model="remarks"
            rows="3"
            maxlength="500"
            placeholder="Enter remarks..."
          ></textarea>

          <div class="remarks-footer">{{ remarks.length }}/500</div>
        </div>
      </div>

      <!-- =====================================================
           ACTION BAR
      ====================================================== -->
      <div class="action-bar">
        <button
          type="button"
          class="btn cancel-button"
          @click="cancelSchedule"
          :disabled="saving"
        >
          Cancel
        </button>

        <button
          type="button"
          class="btn save-button"
          @click="saveSchedule"
          :disabled="saving"
        >
          <span v-if="saving" class="spinner-border spinner-border-sm"></span>

          <span v-else>Save Schedule</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";

import {
  fetchDistributors as fetchDistributorsApi,
  fetchRouteList,
  fetchDeliveryScheduleTimeSlots,
  fetchDealersByRoutes,
  createDeliverySchedule,
} from "@/services/dataService";

export default {
  name: "DispatchSchedule",

  data() {
    const currentDate = new Date().toISOString().split("T")[0];

    return {
      currentDate,

      userRole: localStorage.getItem("user_label"),
      token: localStorage.getItem("token_set"),
      profileId: localStorage.getItem("profile_id"),

      ALL: "__all__",

      loadingDistributors: false,
      loadingRoutes: false,
      loadingTimeSlots: false,
      loadingDealers: false,
      saving: false,

      filtersSearched: false,

      isDistributorLocked: false,

      showRouteDropdown: false,

      selectedDistributorId: "",
      selectedRoutes: [],
      selectedVehicles: [],

      selectedTimeSlot: "",
      effectiveFrom: currentDate,
      effectiveTo: "",

      status: "active",

      dealerSearchText: "",
      dealerList: [],
      selectedDealerIds: [],

      draftDeliveryFrequency: "day_by_day",
      draftDeliveryDays: [],
      draftWeeks: [],
      draftMonths: [],

      remarks: "",

      distributorList: [],
      deliveryRouteList: [],
      vehicleList: [],
      timeSlotList: [],

      deliveryDays: [
        { value: "monday", label: "Mon" },
        { value: "tuesday", label: "Tue" },
        { value: "wednesday", label: "Wed" },
        { value: "thursday", label: "Thu" },
        { value: "friday", label: "Fri" },
        { value: "saturday", label: "Sat" },
        { value: "sunday", label: "Sun" },
      ],

      weekOptions: [
        { value: "week_1", label: "Week 1" },
        { value: "week_2", label: "Week 2" },
        { value: "week_3", label: "Week 3" },
        { value: "week_4", label: "Week 4" },
      ],

      monthOptions: [
        { value: "january", label: "Jan" },
        { value: "february", label: "Feb" },
        { value: "march", label: "Mar" },
        { value: "april", label: "Apr" },
        { value: "may", label: "May" },
        { value: "june", label: "Jun" },
        { value: "july", label: "Jul" },
        { value: "august", label: "Aug" },
        { value: "september", label: "Sep" },
        { value: "october", label: "Oct" },
        { value: "november", label: "Nov" },
        { value: "december", label: "Dec" },
      ],
    };
  },

  setup() {
    const toast = useToast();

    return {
      toast,
    };
  },

  computed: {
    isDistributorUser() {
      const user = String(this.userRole || "")
        .toUpperCase()
        .trim();

      return (
        user === "DISTRIBUTOR" ||
        user.startsWith("DISTRIBUTOR") ||
        user === "DIS" ||
        user.startsWith("DIS_")
      );
    },

    isAllRoutesSelected() {
      return (
        this.deliveryRouteList.length > 0 &&
        this.deliveryRouteList.every((route) =>
          this.selectedRoutes.includes(String(route.id)),
        )
      );
    },

    selectableVehicleList() {
      return this.vehicleList.filter((vehicle) =>
        this.canSelectVehicle(vehicle),
      );
    },

    isAllVehiclesSelected() {
      return (
        this.selectableVehicleList.length > 0 &&
        this.selectableVehicleList.every((vehicle) =>
          this.selectedVehicles.includes(String(vehicle.id)),
        )
      );
    },

    hasDateDraftSelection() {
      return (
        this.draftDeliveryDays.length > 0 ||
        this.draftWeeks.length > 0 ||
        this.draftMonths.length > 0
      );
    },

    selectedDateSet() {
      if (this.draftDeliveryFrequency === "day_by_day") {
        if (!this.draftDeliveryDays.length) {
          return null;
        }

        return {
          type: "day_by_day",
          months: [],
          weeks: [],
          days: [...this.draftDeliveryDays],
        };
      }

      if (this.draftDeliveryFrequency === "week_by_week") {
        if (!this.draftWeeks.length || !this.draftDeliveryDays.length) {
          return null;
        }

        return {
          type: "week_by_week",
          months: [],
          weeks: [...this.draftWeeks],
          days: [...this.draftDeliveryDays],
        };
      }

      if (this.draftDeliveryFrequency === "month_by_month") {
        if (
          !this.draftMonths.length ||
          !this.draftWeeks.length ||
          !this.draftDeliveryDays.length
        ) {
          return null;
        }

        return {
          type: "month_by_month",
          months: [...this.draftMonths],
          weeks: [...this.draftWeeks],
          days: [...this.draftDeliveryDays],
        };
      }

      return null;
    },

    filteredDealerList() {
      const keyword = this.dealerSearchText.trim().toLowerCase();

      if (!keyword) {
        return this.dealerList;
      }

      return this.dealerList.filter((dealer) => {
        const routeText = this.formatDealerRoutes(dealer).toLowerCase();
        const scheduleText = this.getScheduleNameList(dealer)
          .join(" ")
          .toLowerCase();

        return (
          String(dealer.name || "").toLowerCase().includes(keyword) ||
          String(dealer.address || "").toLowerCase().includes(keyword) ||
          String(dealer.address1 || "").toLowerCase().includes(keyword) ||
          String(dealer.area || "").toLowerCase().includes(keyword) ||
          routeText.includes(keyword) ||
          scheduleText.includes(keyword)
        );
      });
    },

    selectableFilteredDealerList() {
      return this.filteredDealerList.filter((dealer) =>
        this.canSelectDealer(dealer),
      );
    },

    isAllDealersSelected() {
      return (
        this.selectableFilteredDealerList.length > 0 &&
        this.selectableFilteredDealerList.every((dealer) =>
          this.selectedDealerIds.includes(String(dealer.id)),
        )
      );
    },
  },

  mounted() {
    document.addEventListener("click", this.closeDropdowns);
    this.fetchDistributors();
    this.loadTimeSlots();
  },

  beforeUnmount() {
    document.removeEventListener("click", this.closeDropdowns);
  },

  methods: {
    closeDropdowns() {
      this.showRouteDropdown = false;
    },

    bodyOf(response) {
      return response && response.data ? response.data : response;
    },

    listOf(body) {
      if (Array.isArray(body)) {
        return body;
      }

      if (body && Array.isArray(body.data)) {
        return body.data;
      }

      if (body && Array.isArray(body.Data)) {
        return body.Data;
      }

      if (body && Array.isArray(body.result)) {
        return body.result;
      }

      if (body && Array.isArray(body.Result)) {
        return body.Result;
      }

      if (body && body.data && Array.isArray(body.data.data)) {
        return body.data.data;
      }

      if (body && body.Data && Array.isArray(body.Data.Data)) {
        return body.Data.Data;
      }

      return [];
    },

    namedListOf(body, keys) {
      const containers = [
        body,
        body && body.data,
        body && body.Data,
        body && body.result,
        body && body.Result,
      ];

      for (const container of containers) {
        if (!container || Array.isArray(container)) {
          continue;
        }

        for (const key of keys) {
          if (Array.isArray(container[key])) {
            return container[key];
          }
        }
      }

      return [];
    },

    isSuccessResponse(body) {
      if (!body || body.status === undefined) {
        return true;
      }

      const status = String(body.status).toLowerCase();

      return (
        status === "success" ||
        status === "true" ||
        status === "1" ||
        body.status === true ||
        body.success === true
      );
    },

    extractDealerAndVehicleLists(body) {
      const dealerList = this.namedListOf(body, [
        "dealers",
        "dealer_list",
        "dealerList",
        "dealer_data",
        "dealerData",
        "dealer_profile_list",
        "dealerProfileList",
      ]);

      const vehicleList = this.namedListOf(body, [
        "vehicles",
        "vehicle_list",
        "vehicleList",
        "vehicle_data",
        "vehicleData",
        "delivery_vehicles",
        "deliveryVehicles",
        "sales_reps",
        "salesRepList",
        "sales_representatives",
        "reps",
      ]);

      return {
        dealers: dealerList.length ? dealerList : this.listOf(body),
        vehicles: vehicleList,
      };
    },

    mapDistributorRow(distributor) {
      const profileId =
        distributor.profile_id ||
        distributor.profileId ||
        distributor.ProfileId ||
        distributor.id ||
        distributor.distributor_id ||
        "";

      const name =
        distributor.name ||
        distributor.distributor_name ||
        distributor.business_name ||
        distributor.business_display_name ||
        distributor.profile_name ||
        `Distributor #${profileId}`;

      return {
        ...distributor,
        profile_id: String(profileId || ""),
        name,
      };
    },

    mapRouteRow(route) {
      return {
        ...route,
        id: String(route.id || ""),
        name: route.route_name || route.name || `Route #${route.id}`,
        description: route.route_name || route.description || "Delivery route",
      };
    },

    mapVehicleRow(vehicle) {
      const vehicleId =
        vehicle.rep_profile_id ||
        vehicle.repProfileId ||
        vehicle.vehicle_id ||
        vehicle.vehicleId ||
        vehicle.profile_id ||
        vehicle.profileId ||
        vehicle.ProfileId ||
        vehicle.id ||
        "";

      const firstName = vehicle.first_name || "";
      const lastName = vehicle.last_name || "";
      const fullName = `${firstName} ${lastName}`.trim();

      const name =
        vehicle.vehicle_name ||
        vehicle.vehicleName ||
        vehicle.vehicle_no ||
        vehicle.vehicleNo ||
        vehicle.vehicle_number ||
        vehicle.registration_no ||
        vehicle.registration_number ||
        vehicle.name ||
        vehicle.sales_rep_name ||
        vehicle.rep_name ||
        vehicle.profile_name ||
        fullName ||
        `Vehicle #${vehicleId}`;

      const code =
        vehicle.vehicle_no ||
        vehicle.vehicleNo ||
        vehicle.vehicle_number ||
        vehicle.registration_no ||
        vehicle.registration_number ||
        vehicle.code ||
        vehicle.mobile ||
        vehicle.employee_no ||
        vehicle.emp_no ||
        "";

      const schedules = this.normalizeSchedules(vehicle.schedules);

      const scheduleIds = Array.isArray(vehicle.schedule_ids)
        ? vehicle.schedule_ids.map((item) => String(item))
        : schedules
            .map((schedule) => schedule.schedule_id)
            .filter((scheduleId) => scheduleId);

      const isScheduled =
        vehicle.is_scheduled === true ||
        vehicle.is_scheduled === 1 ||
        vehicle.is_scheduled === "1" ||
        schedules.length > 0 ||
        scheduleIds.length > 0;

      const inactive =
        vehicle.is_active === false ||
        vehicle.is_active === 0 ||
        String(vehicle.status || "").toLowerCase() === "inactive";

      return {
        ...vehicle,
        id: String(vehicleId || ""),
        vehicle_id: String(vehicleId || ""),
        rep_profile_id: String(vehicleId || ""),
        name,
        code,
        schedules,
        schedule_ids: scheduleIds,
        schedule_names: schedules
          .map((schedule) => schedule.name)
          .filter((name) => name),
        is_scheduled: isScheduled,
        is_active: !inactive,
      };
    },

    mapTimeSlotRow(slot) {
      const id =
        slot.id ||
        slot.time_slot_id ||
        slot.delivery_schedule_time_slot_id ||
        "";

      const label =
        slot.time_slot ||
        slot.label ||
        slot.name ||
        slot.time_slot_name ||
        slot.slot_name ||
        slot.description ||
        `Time Slot #${id}`;

      return {
        ...slot,
        id: String(id || ""),
        label,
        time_slot: label,
        start_time: slot.start_time || slot.from_time || "",
        end_time: slot.end_time || slot.to_time || "",
      };
    },

    mapDealerRow(dealer) {
      const profileId =
        dealer.profile_id ||
        dealer.dealer_profile_id ||
        dealer.dealer_id ||
        dealer.id ||
        "";

      const name =
        dealer.business_display_name ||
        dealer.business_name ||
        dealer.dealer_name ||
        dealer.name ||
        dealer.profile_name ||
        dealer.first_name ||
        `Dealer #${profileId}`;

      const routeIds = Array.isArray(dealer.route_ids)
        ? dealer.route_ids
        : dealer.route_id
        ? [dealer.route_id]
        : [];

      const schedules = this.normalizeSchedules(dealer.schedules);

      const scheduleIds = Array.isArray(dealer.schedule_ids)
        ? dealer.schedule_ids.map((item) => String(item))
        : schedules
            .map((schedule) => schedule.schedule_id)
            .filter((scheduleId) => scheduleId);

      const isScheduled =
        dealer.is_scheduled === true ||
        dealer.is_scheduled === 1 ||
        dealer.is_scheduled === "1" ||
        schedules.length > 0 ||
        scheduleIds.length > 0;

      return {
        ...dealer,
        id: String(profileId || ""),
        profile_id: String(profileId || ""),
        dealer_profile_id: String(profileId || ""),
        name,
        route_ids: routeIds.map((routeId) => String(routeId)),
        route_name: dealer.route_name || dealer.route || "",
        address:
          dealer.address || dealer.address1 || dealer.area || dealer.city || "",
        address1: dealer.address1 || dealer.address || "",
        area: dealer.area || "",
        schedules,
        schedule_ids: scheduleIds,
        schedule_names: schedules
          .map((schedule) => schedule.name)
          .filter((name) => name),
        is_scheduled: isScheduled,
        is_active:
          dealer.is_active === false ||
          dealer.is_active === 0 ||
          dealer.status === "inactive"
            ? false
            : true,
      };
    },

    normalizeSchedules(schedules) {
      if (!Array.isArray(schedules)) {
        return [];
      }

      return schedules
        .map((schedule) => {
          const scheduleId =
            schedule.schedule_id ||
            schedule.id ||
            schedule.delivery_schedule_id ||
            "";

          return {
            schedule_id: String(scheduleId || ""),
            name:
              schedule.name ||
              schedule.schedule_name ||
              schedule.delivery_schedule_name ||
              (scheduleId ? `Schedule #${scheduleId}` : ""),
          };
        })
        .filter((schedule) => schedule.schedule_id || schedule.name);
    },

    getScheduleNameList(row) {
      if (!row) {
        return [];
      }

      if (Array.isArray(row.schedule_names) && row.schedule_names.length) {
        return row.schedule_names;
      }

      if (Array.isArray(row.schedules) && row.schedules.length) {
        return this.normalizeSchedules(row.schedules)
          .map((schedule) => schedule.name)
          .filter((name) => name);
      }

      if (Array.isArray(row.schedule_ids) && row.schedule_ids.length) {
        return row.schedule_ids.map((scheduleId) => `Schedule #${scheduleId}`);
      }

      return [];
    },

    canSelectVehicle(vehicle) {
      if (!vehicle || !vehicle.id) {
        return false;
      }

      return !vehicle.is_scheduled;
    },

    canSelectDealer(dealer) {
      if (!dealer || !dealer.id) {
        return false;
      }

      return !dealer.is_scheduled;
    },

    getAvailabilityLabel(row) {
      if (!row) {
        return "-";
      }

      if (row.is_scheduled) {
        return "Not Available";
      }

      if (row.is_active === false) {
        return "Inactive";
      }

      return "Available";
    },

    getAvailabilityClass(row) {
      if (!row) {
        return "";
      }

      if (row.is_scheduled) {
        return "not-available";
      }

      if (row.is_active === false) {
        return "inactive";
      }

      return "available";
    },

    hasOngoingSchedule(row) {
      if (!row) {
        return false;
      }

      return this.getScheduleNameList(row).length > 0 || !!row.is_scheduled;
    },

    formatOngoingSchedule(row) {
      const scheduleNames = this.getScheduleNameList(row);

      if (!scheduleNames.length) {
        return row && row.is_scheduled ? "Scheduled" : "Not scheduled";
      }

      return scheduleNames.join(", ");
    },

    resetLoadedDealers() {
      this.filtersSearched = false;

      this.dealerSearchText = "";
      this.dealerList = [];
      this.selectedDealerIds = [];

      this.vehicleList = [];
      this.selectedVehicles = [];
    },

    async fetchDistributors() {
      this.loadingDistributors = true;

      try {
        const response = await fetchDistributorsApi(this.profileId, this.token);
        const body = this.bodyOf(response);

        const list = this.listOf(body)
          .map((distributor) => this.mapDistributorRow(distributor))
          .filter((distributor) => distributor.profile_id);

        this.distributorList = [];
        this.selectedDistributorId = "";
        this.isDistributorLocked = false;

        if (this.isDistributorUser && this.profileId) {
          const mine = list.find((distributor) => {
            return Number(distributor.profile_id) === Number(this.profileId);
          });

          if (mine) {
            this.distributorList = [mine];
            this.selectedDistributorId = String(mine.profile_id);
            this.isDistributorLocked = true;

            await this.loadDeliveryRoutes();

            return;
          }
        }

        this.distributorList = list;
      } catch (error) {
        console.error("Error fetching distributors:", error);

        this.distributorList = [];
        this.selectedDistributorId = "";

        this.toast.error("Failed to load distributors.");
      } finally {
        this.loadingDistributors = false;
      }
    },

    async loadTimeSlots() {
      this.loadingTimeSlots = true;

      try {
        const response = await fetchDeliveryScheduleTimeSlots(this.token);
        const body = this.bodyOf(response);

        if (body && body.status !== "success") {
          this.timeSlotList = [];
          this.toast.error(
            body && body.message ? body.message : "Failed to load time slots.",
          );
          return;
        }

        this.timeSlotList = this.listOf(body)
          .map((slot) => this.mapTimeSlotRow(slot))
          .filter((slot) => slot.id);

        if (!this.timeSlotList.length) {
          this.toast.error("No time slots found.");
        }
      } catch (error) {
        console.error("Time slot load error:", error);
        console.error("Time slot API response:", error.response);

        this.timeSlotList = [];

        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Failed to load time slots.";

        this.toast.error(message);
      } finally {
        this.loadingTimeSlots = false;
      }
    },

    async onDistributorChange() {
      this.selectedRoutes = [];
      this.selectedVehicles = [];

      this.deliveryRouteList = [];
      this.vehicleList = [];

      this.showRouteDropdown = false;

      this.resetLoadedDealers();

      if (!this.selectedDistributorId) {
        return;
      }

      if (this.selectedDistributorId === this.ALL) {
        return;
      }

      await this.loadDeliveryRoutes();
    },

    async loadDeliveryRoutes() {
      if (
        !this.selectedDistributorId ||
        this.selectedDistributorId === this.ALL
      ) {
        this.deliveryRouteList = [];
        this.selectedRoutes = [];
        return;
      }

      this.loadingRoutes = true;

      try {
        const response = await fetchRouteList(
          this.selectedDistributorId,
          this.token,
        );

        const body = this.bodyOf(response);

        this.deliveryRouteList = this.listOf(body)
          .map((route) => this.mapRouteRow(route))
          .filter((route) => route.id);

        if (!this.deliveryRouteList.length) {
          this.toast.error(
            "No delivery routes found for selected distributor.",
          );
        }
      } catch (error) {
        console.error("Delivery route load error:", error);

        this.deliveryRouteList = [];
        this.selectedRoutes = [];

        this.toast.error("Failed to load delivery routes.");
      } finally {
        this.loadingRoutes = false;
      }
    },

    toggleRouteDropdown() {
      if (
        !this.selectedDistributorId ||
        this.selectedDistributorId === this.ALL ||
        this.loadingRoutes ||
        this.loadingDealers ||
        this.saving
      ) {
        return;
      }

      this.showRouteDropdown = !this.showRouteDropdown;
    },

    toggleRoute(routeId) {
      const id = String(routeId);

      if (this.selectedRoutes.includes(id)) {
        this.selectedRoutes = this.selectedRoutes.filter((item) => item !== id);
      } else {
        this.selectedRoutes.push(id);
      }

      this.resetLoadedDealers();
    },

    selectAllRoutes() {
      this.selectedRoutes = this.deliveryRouteList.map((route) =>
        String(route.id),
      );

      this.resetLoadedDealers();
    },

    clearRoutes() {
      this.selectedRoutes = [];

      this.resetLoadedDealers();
    },

    toggleAllVehicles(event) {
      const checked = event.target.checked;

      if (checked) {
        this.selectAllVehicles();
      } else {
        this.clearVehicles();
      }
    },

    selectAllVehicles() {
      this.selectedVehicles = this.selectableVehicleList.map((vehicle) =>
        String(vehicle.id),
      );
    },

    clearVehicles() {
      this.selectedVehicles = [];
    },

    clearDealers() {
      this.selectedDealerIds = [];
    },

    selectAllVisibleDealers() {
      const visibleIds = this.selectableFilteredDealerList.map((dealer) =>
        String(dealer.id),
      );

      this.selectedDealerIds = Array.from(
        new Set([...this.selectedDealerIds, ...visibleIds]),
      );
    },

    getRouteName(routeId) {
      const route = this.deliveryRouteList.find((item) => {
        return String(item.id) === String(routeId);
      });

      return route && route.name ? route.name : `Route #${routeId}`;
    },

    formatDealerRoutes(dealer) {
      if (dealer.route_name) {
        return dealer.route_name;
      }

      const routeIds = Array.isArray(dealer.route_ids) ? dealer.route_ids : [];

      if (!routeIds.length) {
        return "-";
      }

      return routeIds.map((routeId) => this.getRouteName(routeId)).join(", ");
    },

    requestFrequencyChange(type) {
      if (this.draftDeliveryFrequency === type) {
        return;
      }

      if (this.hasDateDraftSelection) {
        const confirmed = window.confirm(
          "Changing the delivery date type will clear the current selected date set. Do you want to continue?",
        );

        if (!confirmed) {
          return;
        }
      }

      this.draftDeliveryFrequency = type;
      this.clearDateDraft();
    },

    clearSelectedDateSet() {
      this.clearDateDraft();
    },

    handleScheduleDateChange() {
      this.validateDateRange();
      this.resetLoadedDealers();
    },

    onTimeSlotChange() {
      this.resetLoadedDealers();
    },

    validateScheduleFilters() {
      if (
        !this.selectedDistributorId ||
        this.selectedDistributorId === this.ALL
      ) {
        this.toast.error("Please select Distributor.");
        return false;
      }

      if (!this.selectedRoutes.length) {
        this.toast.error("Please select at least one Delivery Route.");
        return false;
      }

      if (!this.selectedTimeSlot) {
        this.toast.error("Please select Time Slot.");
        return false;
      }

      if (!this.effectiveFrom) {
        this.toast.error("Please select Effective From date.");
        return false;
      }

      if (!this.effectiveTo) {
        this.toast.error("Please select Effective To date.");
        return false;
      }

      if (this.effectiveTo < this.effectiveFrom) {
        this.toast.error("Invalid effective date range.");
        return false;
      }

      return true;
    },

    getSelectedAvailableVehicleIds() {
      return this.selectedVehicles.filter((id) => {
        return this.vehicleList.some((vehicle) => {
          return (
            String(vehicle.id) === String(id) && this.canSelectVehicle(vehicle)
          );
        });
      });
    },

    getSelectedAvailableDealerIds() {
      return this.selectedDealerIds.filter((id) => {
        return this.dealerList.some((dealer) => {
          return String(dealer.id) === String(id) && this.canSelectDealer(dealer);
        });
      });
    },

    validateScheduleDetailsForSave() {
      if (!this.selectedTimeSlot) {
        this.toast.error("Please select Time Slot.");
        return false;
      }

      if (!this.getSelectedAvailableVehicleIds().length) {
        this.toast.error("Please select at least one available Vehicle.");
        return false;
      }

      return true;
    },

    async searchScheduleFilters() {
      if (!this.validateScheduleFilters()) {
        return;
      }

      this.loadingDealers = true;
      this.filtersSearched = false;

      this.dealerList = [];
      this.selectedDealerIds = [];
      this.dealerSearchText = "";

      this.vehicleList = [];
      this.selectedVehicles = [];

      try {
        const payload = {
          route_ids: this.selectedRoutes.map((id) => Number(id)),
          time_slot_id: Number(this.selectedTimeSlot),
          start_date: this.effectiveFrom,
          end_date: this.effectiveTo,
        };

        const response = await fetchDealersByRoutes(payload, this.token);
        const body = this.bodyOf(response);

        if (!this.isSuccessResponse(body)) {
          this.toast.error(body && body.message ? body.message : "Failed to load dealers.");
          return;
        }

        const extracted = this.extractDealerAndVehicleLists(body);

        this.dealerList = extracted.dealers
          .map((dealer) => this.mapDealerRow(dealer))
          .filter((dealer) => dealer.id);

        this.vehicleList = extracted.vehicles
          .map((vehicle) => this.mapVehicleRow(vehicle))
          .filter((vehicle) => vehicle.id);

        this.filtersSearched = true;

        if (this.dealerList.length && this.vehicleList.length) {
          this.toast.success("Dealers and vehicles loaded successfully.");
        } else if (this.dealerList.length) {
          this.toast.success("Dealers loaded successfully. No vehicles found.");
        } else {
          this.toast.error(
            "No dealers found for selected route and date range.",
          );
        }
      } catch (error) {
        console.error("Dealer load error:", error);
        console.error("Dealer API response:", error.response);

        this.dealerList = [];
        this.selectedDealerIds = [];
        this.vehicleList = [];
        this.selectedVehicles = [];

        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Failed to load dealers.";

        this.toast.error(message);
      } finally {
        this.loadingDealers = false;
      }
    },

    toggleAllDealers(event) {
      const checked = event.target.checked;

      const visibleIds = this.selectableFilteredDealerList.map((dealer) =>
        String(dealer.id),
      );

      if (checked) {
        this.selectedDealerIds = Array.from(
          new Set([...this.selectedDealerIds, ...visibleIds]),
        );
      } else {
        this.selectedDealerIds = this.selectedDealerIds.filter((id) => {
          return !visibleIds.includes(String(id));
        });
      }
    },

    clearDateDraft() {
      this.draftDeliveryDays = [];
      this.draftWeeks = [];
      this.draftMonths = [];
    },

    getBatchTypeClass(type) {
      if (type === "day_by_day") {
        return "day-type";
      }

      if (type === "week_by_week") {
        return "week-type";
      }

      if (type === "month_by_month") {
        return "month-type";
      }

      return "";
    },

    getFrequencyLabel(type) {
      if (type === "day_by_day") {
        return "Day by Day";
      }

      if (type === "week_by_week") {
        return "Week By Week";
      }

      if (type === "month_by_month") {
        return "Month By Month";
      }

      return "-";
    },

    getDayLabel(value) {
      const item = this.deliveryDays.find((day) => day.value === value);

      return item && item.label ? item.label : value;
    },

    getWeekLabel(value) {
      const item = this.weekOptions.find((week) => week.value === value);

      return item && item.label ? item.label : value;
    },

    getMonthLabel(value) {
      const item = this.monthOptions.find((month) => month.value === value);

      return item && item.label ? item.label : value;
    },

    formatDeliveryBatch(batch) {
      const parts = [];

      if (batch.months && batch.months.length) {
        parts.push(
          batch.months.map((month) => this.getMonthLabel(month)).join(", "),
        );
      }

      if (batch.weeks && batch.weeks.length) {
        parts.push(
          batch.weeks.map((week) => this.getWeekLabel(week)).join(", "),
        );
      }

      if (batch.days && batch.days.length) {
        parts.push(batch.days.map((day) => this.getDayLabel(day)).join(", "));
      }

      return parts.join(" • ");
    },

    validateDateRange() {
      if (
        this.effectiveFrom &&
        this.effectiveTo &&
        this.effectiveTo < this.effectiveFrom
      ) {
        this.toast.error("Effective To cannot be before Effective From.");
        this.effectiveTo = "";
      }
    },

    getRecurrenceTypeForApi(type) {
      if (type === "day_by_day") {
        return "day";
      }

      if (type === "week_by_week") {
        return "week";
      }

      if (type === "month_by_month") {
        return "month";
      }

      return "";
    },

    getMonthNumber(value) {
      const monthMap = {
        january: 1,
        february: 2,
        march: 3,
        april: 4,
        may: 5,
        june: 6,
        july: 7,
        august: 8,
        september: 9,
        october: 10,
        november: 11,
        december: 12,
      };

      return monthMap[value] || Number(value) || null;
    },

    getWeekNumber(value) {
      if (typeof value === "number") {
        return value;
      }

      return Number(String(value).replace("week_", "")) || null;
    },

    getWeekdayNumber(value) {
      const weekdayMap = {
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
        sunday: 7,
      };

      return weekdayMap[value] || Number(value) || null;
    },

    cleanNumberList(list) {
      return list.filter((value) => value !== null && value !== undefined);
    },

    validateSchedule() {
      if (!this.validateScheduleFilters()) {
        return false;
      }

      if (!this.validateScheduleDetailsForSave()) {
        return false;
      }

      if (!this.filtersSearched) {
        this.toast.error("Please click Search to load dealers and vehicles.");
        return false;
      }

      if (!this.getSelectedAvailableDealerIds().length) {
        this.toast.error("Please select at least one available Dealer.");
        return false;
      }

      if (!this.selectedDateSet) {
        this.toast.error("Please complete one Delivery Date Set.");
        return false;
      }

      return true;
    },

    async saveSchedule() {
      if (!this.validateSchedule()) {
        return;
      }

      this.saving = true;

      try {
        const selectedAvailableVehicleIds = this.getSelectedAvailableVehicleIds();
        const selectedAvailableDealerIds = this.getSelectedAvailableDealerIds();

        const payload = {
          distributor_profile_id: Number(this.selectedDistributorId),
          created_by: Number(this.profileId || this.selectedDistributorId),

          route_ids: this.selectedRoutes.map((id) => Number(id)),
          vehicle_ids: selectedAvailableVehicleIds.map((id) => Number(id)),

          time_slot_id: Number(this.selectedTimeSlot),

          start_date: this.effectiveFrom,
          end_date: this.effectiveTo,

          dealer_profile_ids: selectedAvailableDealerIds.map((id) =>
            Number(id),
          ),

          recurrence_type: this.getRecurrenceTypeForApi(
            this.selectedDateSet.type,
          ),

          remarks: this.remarks.trim(),

          months: this.cleanNumberList(
            (this.selectedDateSet.months || []).map((month) =>
              this.getMonthNumber(month),
            ),
          ),

          weeks: this.cleanNumberList(
            (this.selectedDateSet.weeks || []).map((week) =>
              this.getWeekNumber(week),
            ),
          ),

          weekdays: this.cleanNumberList(
            (this.selectedDateSet.days || []).map((day) =>
              this.getWeekdayNumber(day),
            ),
          ),
        };

        console.log("Create Delivery Schedule Payload:", payload);

        const response = await createDeliverySchedule(payload, this.token);
        const body = this.bodyOf(response);

        if (
          body &&
          (body.status === "success" ||
            body.success === true ||
            body.message === "success")
        ) {
          this.toast.success(
            body.message || "Delivery schedule created successfully.",
          );

          await this.cancelSchedule();
          return;
        }

        this.toast.error(
          body && body.message
            ? body.message
            : "Failed to create delivery schedule.",
        );
      } catch (error) {
        console.error("Create delivery schedule error:", error);

        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Failed to create delivery schedule.";

        this.toast.error(message);
      } finally {
        this.saving = false;
      }
    },

    async cancelSchedule() {
      this.selectedRoutes = [];
      this.selectedVehicles = [];

      this.deliveryRouteList = [];
      this.vehicleList = [];

      this.selectedTimeSlot = "";
      this.status = "active";
      this.effectiveFrom = this.currentDate;
      this.effectiveTo = "";

      this.filtersSearched = false;

      this.dealerSearchText = "";
      this.dealerList = [];
      this.selectedDealerIds = [];

      this.draftDeliveryFrequency = "day_by_day";
      this.clearDateDraft();

      this.remarks = "";
      this.showRouteDropdown = false;

      if (this.isDistributorLocked && this.distributorList.length) {
        this.selectedDistributorId = String(this.distributorList[0].profile_id);
        await this.loadDeliveryRoutes();
        return;
      }

      this.selectedDistributorId = "";
      await this.fetchDistributors();
    },
  },
};
</script>

<style scoped>
.dispatch-schedule-page {
  --old-search-blue: #007bff;
  --old-search-blue-hover: #0056b3;
  --dark-blue: #0056b3;
  --dark-blue-hover: #00458f;
  --dark-blue-soft: rgba(0, 86, 179, 0.1);
  --dark-blue-selected-row: rgba(0, 86, 179, 0.08);
  --dark-blue-border: rgba(0, 86, 179, 0.3);

  padding-bottom: 24px;
}

.dispatch-schedule-container {
  width: calc(100% - 20px);
  margin: 10px;
  padding: 16px;
  box-sizing: border-box;
  overflow: visible;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03),
    0 4px 18px rgba(15, 23, 42, 0.06);
}

.section-card {
  position: relative;
  margin-bottom: 13px;
  padding: 13px;
  overflow: visible;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  background: #ffffff;
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 11px;
}

.section-header h6 {
  margin: 0;
  color: #111827;
  font-size: 13.5px;
  font-weight: 800;
}

.section-header > div > span {
  display: block;
  margin-top: 2px;
  color: #6b7280;
  font-size: 11.5px;
}

.section-count {
  flex-shrink: 0;
  padding: 4px 8px;
  border: 1px solid var(--dark-blue-border);
  border-radius: 999px;
  background: var(--dark-blue);
  color: #ffffff;
  font-size: 10.5px;
  font-weight: 800;
}

.optional-label {
  padding: 3px 7px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 10px;
  font-weight: 800;
}

.schedule-main-grid {
  display: grid;
  grid-template-columns:
    minmax(185px, 1.05fr)
    minmax(230px, 1.45fr)
    minmax(135px, 0.7fr)
    minmax(135px, 0.7fr)
    minmax(150px, 0.75fr)
    minmax(105px, 0.42fr);
  gap: 10px;
  align-items: end;
}

.route-item {
  min-width: 0;
}

.schedule-search-item {
  min-width: 96px;
}

.schedule-search-item .btn {
  width: 100%;
}

.form-item {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-item label {
  display: block;
  margin-bottom: 4px;
  color: #374151;
  font-size: 11.5px;
  font-weight: 800;
}

.form-item input,
.form-item select,
.form-item textarea {
  width: 100%;
  height: 34px;
  min-height: 34px;
  padding: 6px 9px;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  outline: none;
  background: #ffffff;
  color: #111827;
  font-size: 12.5px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background 0.15s ease;
}

.form-item input:focus,
.form-item select:focus,
.form-item textarea:focus {
  border-color: var(--dark-blue);
  box-shadow: 0 0 0 2px var(--dark-blue-soft);
}

.form-item input:disabled,
.form-item select:disabled {
  background: #f3f4f6;
  color: #6b7280;
  cursor: not-allowed;
}

.hidden-label {
  visibility: hidden;
}

.search-input-wrap {
  position: relative;
}

.search-input-wrap > i {
  position: absolute;
  top: 50%;
  left: 10px;
  z-index: 2;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 11px;
  pointer-events: none;
}

.search-input-wrap input {
  padding-left: 29px;
}

.smart-multi {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 34px;
  min-height: 34px;
  padding: 5px 34px 5px 8px;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfbfc 100%);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background 0.15s ease;
}

.smart-multi:hover:not(.disabled) {
  border-color: #9ca3af;
  background: #ffffff;
}

.smart-multi.open {
  border-color: var(--dark-blue);
  box-shadow: 0 0 0 2px var(--dark-blue-soft);
}

.smart-multi.disabled {
  background: #f3f4f6;
  cursor: not-allowed;
  opacity: 0.75;
}

.smart-multi-content {
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.smart-placeholder {
  color: #6b7280;
  font-size: 12.5px;
}

.smart-chip-list {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
}

.smart-chip,
.smart-chip-more {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 3px 7px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 900;
}

.smart-chip {
  max-width: 120px;
  overflow: hidden;
  background: var(--dark-blue);
  color: #ffffff;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.smart-chip-more {
  background: #f3f4f6;
  color: #374151;
}

.smart-chevron {
  position: absolute;
  right: 10px;
  color: #64748b;
  font-size: 10px;
}

.smart-menu {
  position: absolute;
  top: calc(100% + 6px);
  z-index: 9999;
  width: min(420px, calc(100vw - 48px));
  max-height: min(360px, calc(100vh - 160px));
  overflow: hidden;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 18px 38px rgba(15, 23, 42, 0.16),
    0 4px 12px rgba(15, 23, 42, 0.08);
}

.smart-menu-left {
  left: 0;
  right: auto;
}

.smart-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 10px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}

.smart-menu-header > div:first-child {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.smart-menu-header strong {
  color: #111827;
  font-size: 11.5px;
  font-weight: 900;
}

.smart-menu-header span {
  margin-top: 1px;
  color: #6b7280;
  font-size: 10px;
  font-weight: 700;
}

.smart-header-actions {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}

.smart-header-actions button {
  height: 24px;
  padding: 0 8px;
  border: 1px solid var(--dark-blue-border);
  border-radius: 999px;
  background: #ffffff;
  color: var(--dark-blue);
  font-size: 10px;
  font-weight: 900;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.smart-header-actions button:hover:not(:disabled) {
  border-color: var(--dark-blue);
  background: var(--dark-blue);
  color: #ffffff;
}

.smart-header-actions button:disabled {
  border-color: #e5e7eb;
  background: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
}

.smart-option-list {
  max-height: min(270px, calc(100vh - 230px));
  overflow: auto;
  padding: 6px;
}

.smart-option {
  width: 100%;
  display: grid;
  grid-template-columns: 22px 1fr;
  align-items: center;
  gap: 9px;
  min-height: 42px;
  margin: 0;
  padding: 8px 9px;
  border: none;
  border-radius: 8px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
}

.smart-option:hover {
  background: #f8fafc;
}

.smart-option.selected {
  background: var(--dark-blue);
}

.smart-option.selected:hover {
  background: var(--dark-blue-hover);
}

.smart-option.selected .smart-option-text strong,
.smart-option.selected .smart-option-text small {
  color: #ffffff;
}

.smart-check {
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  color: transparent;
  font-size: 9px;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.smart-option.selected .smart-check {
  border-color: #ffffff;
  background: #ffffff;
  color: var(--dark-blue);
}

.smart-option-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.smart-option-text strong {
  overflow: hidden;
  color: #111827;
  font-size: 11.8px;
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.smart-option-text small {
  overflow: hidden;
  margin-top: 1px;
  color: #6b7280;
  font-size: 9.8px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.smart-empty {
  padding: 18px 10px;
  text-align: center;
  color: #6b7280;
  font-size: 12px;
}

.btn {
  height: 34px;
  min-height: 34px;
  padding: 6px 13px;
  border: none;
  border-radius: 5px;
  color: #ffffff;
  font-size: 12.5px;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.3s, opacity 0.15s ease, transform 0.15s ease,
    box-shadow 0.15s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  opacity: 0.96;
}

.btn:disabled {
  background: #6c757d !important;
  cursor: not-allowed;
  opacity: 0.7;
  transform: none;
}

.dispatch-schedule-page .btn.button-default {
  background-color: var(--old-search-blue) !important;
  color: #ffffff !important;
}

.dispatch-schedule-page .btn.button-default:hover:not(:disabled) {
  background-color: var(--old-search-blue-hover) !important;
  color: #ffffff !important;
}

.dispatch-schedule-page .btn.button-default:active:not(:disabled) {
  transform: scale(0.98);
}

.save-button {
  min-width: 130px;
  background: #28a745;
}

.cancel-button {
  min-width: 90px;
  background: #6c757d;
}

.mini-action-button {
  height: 25px;
  padding: 0 9px;
  border: 1px solid var(--dark-blue-border);
  border-radius: 999px;
  background: #ffffff;
  color: var(--dark-blue);
  font-size: 10px;
  font-weight: 900;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.mini-action-button:hover:not(:disabled) {
  border-color: var(--dark-blue);
  background: var(--dark-blue);
  color: #ffffff;
}

.mini-action-button:disabled {
  border-color: #e5e7eb;
  background: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
}

.dealer-filter-grid {
  display: grid;
  grid-template-columns: minmax(280px, 1fr);
  gap: 10px;
  align-items: end;
  margin-bottom: 10px;
}

.selection-table-card {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #ffffff;
}

.vehicle-selection-card {
  margin-top: 2px;
}

.vehicle-wait-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 58px;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  background: #fafafa;
  color: #6b7280;
  font-size: 12px;
  font-weight: 300;
}

.selection-table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.toolbar-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-title strong {
  color: #111827;
  font-size: 12.5px;
}

.toolbar-title span {
  color: #6b7280;
  font-size: 10.5px;
  font-weight: 700;
}

.vehicle-table-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.selected-count-badge {
  padding: 3px 7px;
  border-radius: 999px;
  background: var(--dark-blue);
  color: #ffffff;
  font-size: 10px;
  font-weight: 800;
}

.table-scroll {
  overflow: auto;
  scrollbar-gutter: stable;
}

.dealer-table-scroll {
  max-height: 330px;
}

.vehicle-table-scroll {
  max-height: 260px;
}

.selection-table {
  width: 100%;
  margin: 0;
  border-collapse: separate;
  border-spacing: 0;
}

.dealer-table {
  min-width: 1080px;
}

.vehicle-table {
  min-width: 960px;
}

.selection-table th,
.selection-table td {
  padding: 8px 9px;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  color: #111827;
  font-size: 12.5px;
  white-space: nowrap;
}

.selection-table thead th {
  position: sticky;
  top: 0;
  z-index: 4;
  background: #f9fafb;
  color: #374151;
  font-weight: 800;
}

.selection-table tbody tr:hover td {
  background: #f8fafc;
}

.selection-table tbody tr.selected td {
  background: var(--dark-blue-selected-row);
}

.selection-table tbody tr.disabled-row td {
  background: #f9fafb;
  color: #9ca3af;
}

.selection-table tbody tr.disabled-row:hover td {
  background: #f9fafb;
}

.selection-table tbody tr.disabled-row input[type="checkbox"] {
  cursor: not-allowed;
}

.select-column {
  width: 46px;
  text-align: center;
}

.primary-cell {
  font-weight: 800;
}

.vehicle-code {
  display: inline-block;
  padding: 3px 7px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 10.5px;
  font-weight: 800;
}

.route-cell {
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  color: var(--dark-blue);
  font-size: 11.5px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.address-cell {
  max-width: 360px;
  color: #374151;
  white-space: normal !important;
  line-height: 1.35;
}

.schedule-list-cell {
  min-width: 260px;
  max-width: 420px;
  white-space: normal !important;
}

.schedule-chip-list {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 5px;
  width: 100%;
  max-width: 410px;
  max-height: 76px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 3px;
}

.schedule-name-chip {
  display: inline-flex;
  align-items: center;
  max-width: 180px;
  overflow: hidden;
  padding: 3px 7px;
  border: 1px solid #fed7aa;
  border-radius: 999px;
  background: #fff7ed;
  color: #c2410c;
  font-size: 9.5px;
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.no-schedule-text {
  color: #9ca3af;
  font-size: 10px;
  font-weight: 800;
}

.availability-pill {
  display: inline-block;
  min-width: 86px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 900;
  text-align: center;
}

.availability-pill.available {
  background: #dcfce7;
  color: #166534;
}

.availability-pill.not-available {
  background: #fee2e2;
  color: #991b1b;
}

.availability-pill.inactive {
  background: #f3f4f6;
  color: #6b7280;
}

.empty-table {
  padding: 22px 10px !important;
  text-align: center;
  color: #6b7280 !important;
}

.date-set-builder {
  padding: 11px;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  background: #fafafa;
}

.date-set-builder-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.date-set-builder-header > div {
  display: flex;
  flex-direction: column;
}

.date-set-builder-header strong {
  color: #111827;
  font-size: 12px;
}

.date-set-builder-header span {
  margin-top: 2px;
  color: #6b7280;
  font-size: 10.5px;
}

.frequency-selector {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.frequency-option {
  position: relative;
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  min-height: 54px;
  margin: 0;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 7px;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.15s ease, background 0.15s ease,
    box-shadow 0.15s ease;
}

.frequency-option:hover {
  border-color: #b6bdc7;
  background: #fafafa;
}

.frequency-option.selected {
  border-color: var(--dark-blue);
  background: rgba(0, 86, 179, 0.06);
  box-shadow: 0 0 0 2px var(--dark-blue-soft);
}

.frequency-radio {
  position: relative;
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  border: 1.5px solid #9ca3af;
  border-radius: 50%;
}

.frequency-option.selected .frequency-radio {
  border-color: var(--dark-blue);
}

.frequency-option.selected .frequency-radio::after {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--dark-blue);
  content: "";
}

.frequency-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.frequency-content strong {
  color: #111827;
  font-size: 12px;
  font-weight: 800;
}

.frequency-content small {
  margin-top: 1px;
  color: #6b7280;
  font-size: 10.5px;
  font-weight: 500;
}

.date-config-panel {
  padding: 11px;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  background: #ffffff;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.month-config-stack {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.config-block {
  min-width: 0;
}

.config-label-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 7px;
}

.config-label-row strong {
  display: block;
  color: #111827;
  font-size: 11.5px;
  font-weight: 800;
}

.config-label-row span {
  display: block;
  margin-top: 1px;
  color: #6b7280;
  font-size: 10.5px;
}

.config-label-row > small {
  color: #6b7280;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}

.chip-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.choice-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 62px;
  height: 31px;
  margin: 0;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #ffffff;
  color: #374151;
  font-size: 11.5px;
  font-weight: 800;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease,
    transform 0.15s ease;
}

.choice-chip:hover {
  border-color: #9ca3af;
  transform: translateY(-1px);
}

.choice-chip input {
  display: none;
}

.choice-chip.selected {
  border-color: var(--dark-blue);
  background: var(--dark-blue);
  color: #ffffff;
}

.month-selector {
  display: grid;
  grid-template-columns: repeat(12, minmax(48px, 1fr));
}

.month-chip {
  min-width: 0;
  width: 100%;
}

.added-batches-section {
  margin-top: 12px;
}

.batch-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
}

.batch-section-header > div {
  display: flex;
  align-items: center;
  gap: 7px;
}

.batch-section-header strong {
  color: #111827;
  font-size: 11.5px;
}

.batch-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.batch-card {
  display: grid;
  grid-template-columns: 27px 1fr 28px;
  align-items: center;
  gap: 8px;
  min-height: 47px;
  padding: 6px 7px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #ffffff;
}

.batch-index {
  display: grid;
  place-items: center;
  width: 25px;
  height: 25px;
  border-radius: 5px;
  background: var(--dark-blue);
  color: #ffffff;
  font-size: 10.5px;
  font-weight: 900;
}

.batch-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.batch-type {
  width: fit-content;
  margin-bottom: 2px;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: 8.5px;
  font-weight: 900;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.batch-type.day-type {
  background: #dcfce7;
  color: #166534;
}

.batch-type.week-type {
  background: var(--dark-blue);
  color: #ffffff;
}

.batch-type.month-type {
  background: #f3e8ff;
  color: #7e22ce;
}

.batch-content strong {
  overflow: hidden;
  color: #111827;
  font-size: 11.5px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.batch-remove-button {
  display: grid;
  place-items: center;
  width: 27px;
  height: 27px;
  padding: 0;
  border: none;
  border-radius: 5px;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.batch-remove-button:hover {
  background: #fef2f2;
  color: #dc2626;
}

.no-date-sets {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 48px;
  margin-top: 10px;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  background: #fafafa;
  color: #6b7280;
  font-size: 10.5px;
  font-weight: 300;
}

.remarks-box textarea {
  width: 100%;
  min-height: 76px;
  padding: 8px 10px;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  outline: none;
  background: #ffffff;
  color: #111827;
  font-size: 12.5px;
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.remarks-box textarea:focus {
  border-color: var(--dark-blue);
  box-shadow: 0 0 0 2px var(--dark-blue-soft);
}

.remarks-footer {
  margin-top: 3px;
  text-align: right;
  color: #9ca3af;
  font-size: 9.5px;
}

.action-bar {
  position: sticky;
  bottom: 0;
  z-index: 20;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 0 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 28%);
}

.dispatch-schedule-page,
.dispatch-schedule-page * {
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 116, 139, 0.34) transparent;
}

.dispatch-schedule-page *::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.dispatch-schedule-page *::-webkit-scrollbar-track {
  background: transparent;
}

.dispatch-schedule-page *::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(100, 116, 139, 0.27);
}

.dispatch-schedule-page *::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.55);
}

.dispatch-schedule-page *::-webkit-scrollbar-corner {
  background: transparent;
}

.required-star {
  display: inline !important;
  margin-left: 3px;
  color: #dc2626 !important;
  font-size: 10.5px;
  font-weight: 400 !important;
  line-height: 1;
  vertical-align: text-top;
}

.section-header h6 .required-star {
  display: inline !important;
  margin-left: 4px;
  color: #dc2626 !important;
  font-size: 10.5px;
  font-weight: 400 !important;
}

.form-item label .required-star,
.toolbar-title strong .required-star,
.date-set-builder-header strong .required-star,
.config-label-row strong .required-star {
  display: inline !important;
  margin-left: 3px;
  color: #dc2626 !important;
  font-size: 10.5px;
  font-weight: 400 !important;
}

@media (max-width: 1350px) {
  .schedule-main-grid {
    grid-template-columns: repeat(3, minmax(210px, 1fr));
  }

  .schedule-search-item {
    grid-column: span 1;
  }
}

@media (max-width: 1200px) {
  .schedule-list-cell {
    min-width: 230px;
    max-width: 340px;
  }

  .schedule-chip-list {
    max-width: 330px;
    max-height: 86px;
  }

  .schedule-name-chip {
    max-width: 150px;
  }
}

@media (max-width: 1100px) {
  .smart-menu {
    position: fixed;
    left: 14px !important;
    right: 14px !important;
    top: auto;
    bottom: 74px;
    width: auto;
    max-width: none;
    max-height: 72vh;
    border-radius: 14px;
  }

  .smart-menu-left {
    left: 14px !important;
    right: 14px !important;
  }

  .smart-option-list {
    max-height: 52vh;
  }

  .smart-menu-header {
    align-items: flex-start;
  }
}

@media (max-width: 900px) {
  .dispatch-schedule-container {
    width: calc(100% - 12px);
    margin: 6px;
    padding: 12px;
  }

  .schedule-main-grid,
  .dealer-filter-grid {
    grid-template-columns: 1fr;
  }

  .frequency-selector {
    grid-template-columns: 1fr;
  }

  .config-grid {
    grid-template-columns: 1fr;
  }

  .month-selector {
    grid-template-columns: repeat(4, minmax(48px, 1fr));
  }

  .action-bar {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .action-bar .btn {
    width: 100%;
  }

  .selection-table-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .vehicle-table-actions {
    width: 100%;
  }

  .vehicle-table-actions .mini-action-button {
    width: 100%;
  }
}

@media (max-width: 700px) {
  .schedule-list-cell {
    min-width: 210px;
    max-width: 280px;
  }

  .schedule-chip-list {
    max-width: 270px;
    max-height: 92px;
  }

  .schedule-name-chip {
    max-width: 128px;
    padding: 3px 6px;
    font-size: 9px;
  }
}

@media (max-width: 520px) {
  .month-selector {
    grid-template-columns: repeat(3, minmax(48px, 1fr));
  }

  .choice-chip {
    min-width: calc(50% - 3px);
  }

  .month-chip {
    min-width: 0;
  }

  .action-bar {
    grid-template-columns: 1fr;
  }

  .section-header {
    gap: 8px;
  }

  .smart-menu {
    left: 10px !important;
    right: 10px !important;
    bottom: 64px;
  }
}
</style>